import { useState } from "react";
import { workspaceRecords, workspaceAction, actionKey, type OperationReceipt } from "../../api/hub/workspace";
import { taskDate, taskOverdue } from "./time";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hubKeys } from "../../api/hub/client";
import { getMe, listPeople, getSettings } from "../../api/hub/identity";
import {
  type TaskPatch,
  listProjects,
  listViews,
  createTask,
  updateTask,
  batchTasks,
  saveView,
} from "../../api/hub/work";
import type { Task } from "../../api/hub/types";
export const priorityLabels = ["未定", "紧急", "高", "中", "低"];
export const statusLabels = {
  triage: "待分拣",
  todo: "待办",
  in_progress: "进行中",
  in_review: "待验收",
  done: "已完成",
  canceled: "已取消",
};
export const dateOnly = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const taskSort = (a: Task, b: Task) =>
  (a.priority || 5) - (b.priority || 5) ||
  (a.prioritySortOrder !== undefined && b.prioritySortOrder !== undefined
    ? a.prioritySortOrder - b.prioritySortOrder
    : 0) ||
  (a.dueDate || "9999").localeCompare(b.dueDate || "9999") ||
  a.createdAt - b.createdAt;
export type TaskFilters = {
  person: string;
  priority: string;
  status: string;
  project: string;
  domain: string;
  due: string;
  text: string;
};
export const emptyFilters: TaskFilters = {
  person: "",
  priority: "",
  status: "",
  project: "",
  domain: "",
  due: "",
  text: "",
};
export function filterTasks(tasks: Task[], f: TaskFilters, today = dateOnly(), timeZone = "Asia/Shanghai") {
  return tasks
    .filter(
      (t) =>
        (!f.person ||
          (f.person === "unassigned"
            ? !t.assigneeId
            : t.assigneeId === f.person)) &&
        (!f.priority || String(t.priority) === f.priority) &&
        (!f.status || t.status === f.status) &&
        (!f.project || t.projectId === f.project) &&
        (!f.domain || t.domain === f.domain) &&
        (!f.text ||
          `${t.title} ${t.identifier}`
            .toLowerCase()
            .includes(f.text.toLowerCase())) &&
        (!f.due ||
          (f.due === "none"
            ? !t.dueDate
            : f.due === "overdue"
              ? !!t.dueDate &&
                taskOverdue(t.dueDate, today, timeZone) &&
                !["done", "canceled"].includes(t.status)
              : f.due === "today"
                ? !!t.dueDate && taskDate(t.dueDate, timeZone) === today
                : !!t.dueDate && taskDate(t.dueDate, timeZone) >= today)),
    )
    .sort(taskSort);
}
export function useWorkDirectory() {
  return {
    people: useQuery({ queryKey: hubKeys.people, queryFn: listPeople }),
    me: useQuery({ queryKey: hubKeys.me, queryFn: getMe }),
    settings: useQuery({ queryKey: ["hub", "settings"], queryFn: getSettings }),
    projects: useQuery({ queryKey: hubKeys.projects, queryFn: listProjects }),
  };
}
export function useTasks() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: hubKeys.all });
  const [receipt, setReceipt] = useState<OperationReceipt | null>(null);
  return {
    receipt,
    tasks: useQuery({ queryKey: [...hubKeys.tasks, "include-triage"], queryFn: async () => (await workspaceRecords<Task>({ source: 'tasks', filters: { includeTriage: true } })).items }),
    views: useQuery({ queryKey: ["hub", "views"], queryFn: listViews }),
    create: useMutation({ mutationFn: createTask, onSuccess: invalidate }),
    update: useMutation({
      mutationFn: async ({ id, patch }: { id: string; patch: TaskPatch }) => {
        const { expectedVersion, expectedUpdatedAt: _timestamp, ...changes } = patch;
        const supported = new Set(['title', 'description', 'projectId', 'domain', 'sourceUrl', 'status', 'priority', 'dueDate', 'completionEvidence', 'milestoneId']);
        // Assignment/followers retain their existing dedicated protocol; old unversioned records retain compatibility.
        if (!expectedVersion || Object.keys(changes).some(key => !supported.has(key))) return updateTask(id, patch);
        const result = await workspaceAction<Task>({ action: 'task.edit', input: { id, expectedVersion, changes }, idempotencyKey: actionKey() });
        setReceipt(result.receipt); return result.data;
      },
      onSuccess: invalidate,
    }),
    batch: useMutation({
      mutationFn: ({ ids, patch }: { ids: string[]; patch: Partial<Task> }) =>
        batchTasks(ids, patch),
      onSuccess: invalidate,
    }),
    save: useMutation({
      mutationFn: ({
        name,
        filters,
        layout,
      }: {
        name: string;
        filters: Record<string, string>;
        layout: string;
      }) => saveView(name, filters, layout),
      onSuccess: invalidate,
    }),
  };
}
